import React, { useState, useRef, useEffect } from 'react';
import { ListView } from '../../types/api';

interface ViewSelectorProps {
    privateViews: ListView[];
    publicViews: ListView[];
    currentViewId: number | null;
    baseViewId?: number | null; // The original view that was loaded, used for save functionality
    onSelectView: (view: ListView) => void;
    onSaveView: () => void;
    onSaveAsView: () => void;
    onDeleteView: (viewId: number) => void;
    isLoading?: boolean;
    isAdmin?: boolean;
}

export const ViewSelector: React.FC<ViewSelectorProps> = ({
    privateViews,
    publicViews,
    currentViewId,
    baseViewId,
    onSelectView,
    onSaveView,
    onSaveAsView,
    onDeleteView,
    isLoading = false,
    isAdmin = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const currentView = [...privateViews, ...publicViews].find(v => v.id === currentViewId);
    const displayName = currentView?.name || 'Default View';

    return (
        <div ref={dropdownRef} className="relative inline-block">
            {/* Trigger Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                disabled={isLoading}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 min-w-[180px] justify-between hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
                <span className="overflow-hidden text-ellipsis whitespace-nowrap flex-1 text-left">
                    {isLoading ? 'Loading...' : displayName}
                </span>
                <span className="text-xs">▼</span>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg min-w-[280px] max-h-[400px] overflow-y-auto z-[1000]">
                    {/* Save and Save As Options */}
                    <div className="p-3 border-b border-gray-200 flex flex-col gap-2">
                        <button
                            onClick={() => {
                                onSaveView();
                                setIsOpen(false);
                            }}
                            disabled={baseViewId === null}
                            className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white rounded-md text-sm font-semibold flex items-center justify-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            💾 Save
                        </button>
                        <button
                            onClick={() => {
                                onSaveAsView();
                                setIsOpen(false);
                            }}
                            className="w-full px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md text-sm font-semibold flex items-center justify-center gap-1.5"
                        >
                            ➕ Save As
                        </button>
                    </div>

                    {/* My Views Section */}
                    {privateViews.length > 0 && (
                        <>
                            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-100">
                                My Views
                            </div>
                            {privateViews.map((view) => (
                                <ViewItem
                                    key={view.id}
                                    view={view}
                                    isSelected={view.id === currentViewId}
                                    onSelect={() => {
                                        onSelectView(view);
                                        setIsOpen(false);
                                    }}
                                    onDelete={onDeleteView}
                                />
                            ))}
                        </>
                    )}

                    {/* Public Views Section */}
                    {publicViews.length > 0 && (
                        <>
                            <div className={`px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-100 ${privateViews.length > 0 ? 'mt-2' : ''}`}>
                                Public Views
                            </div>
                            {publicViews.map((view) => (
                                <ViewItem
                                    key={view.id}
                                    view={view}
                                    isSelected={view.id === currentViewId}
                                    onSelect={() => {
                                        onSelectView(view);
                                        setIsOpen(false);
                                    }}
                                    isPublic
                                    onDelete={isAdmin ? onDeleteView : undefined}
                                />
                            ))}
                        </>
                    )}

                    {/* Empty State */}
                    {privateViews.length === 0 && publicViews.length === 0 && (
                        <div className="py-5 px-4 text-center text-sm text-gray-400">
                            No saved views yet.
                            <br />
                            Save your first view to get started!
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

interface ViewItemProps {
    view: ListView;
    isSelected: boolean;
    onSelect: () => void;
    onDelete?: (viewId: number) => void;
    isPublic?: boolean;
}

const ViewItem: React.FC<ViewItemProps> = ({
    view,
    isSelected,
    onSelect,
    onDelete,
    isPublic = false,
}) => {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (showDeleteConfirm) {
            onDelete?.(view.id);
            setShowDeleteConfirm(false);
        } else {
            setShowDeleteConfirm(true);
            setTimeout(() => setShowDeleteConfirm(false), 3000);
        }
    };

    return (
        <div
            onClick={onSelect}
            className={`px-4 py-2.5 cursor-pointer flex items-center justify-between transition-colors border-l-3 ${isSelected
                ? 'bg-blue-50 border-l-blue-500'
                : 'bg-white border-l-transparent hover:bg-gray-50'
                }`}
        >
            <div className="flex-1 min-w-0">
                <div className={`text-sm text-gray-800 overflow-hidden text-ellipsis whitespace-nowrap ${isSelected ? 'font-semibold' : 'font-medium'
                    }`}>
                    {isSelected && '✓ '}
                    {view.name}
                    {view.data.is_default && (
                        <span className="ml-1.5 text-[11px] font-normal">
                            ⭐
                        </span>
                    )}
                    {isPublic && !view.data.is_default && (
                        <span className="ml-1.5 text-[11px] text-gray-500 font-normal">
                            🌐
                        </span>
                    )}
                </div>
                {view.data.description && (
                    <div className="text-xs text-gray-500 mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap">
                        {view.data.description}
                    </div>
                )}
            </div>
            {onDelete && (
                <button
                    onClick={handleDelete}
                    className={`ml-2 px-2 py-1 rounded text-xs cursor-pointer ${showDeleteConfirm
                        ? 'border border-red-400 bg-red-50 text-red-600 font-semibold'
                        : 'border border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                >
                    {showDeleteConfirm ? '✓ Delete?' : '×'}
                </button>
            )}
        </div>
    );
};
